import { img } from "../images";
import type { Blog } from "./types";

const DR_VANITHA = "Dr. M. Vanitha";
const VANITHA_BIO =
  "Dr. M. Vanitha is a Dermatologist at AV Clinic, Krishnagiri (M.B.B.S., D.D.V.L.). She provides specialist care for a wide range of skin, hair and nail conditions — from acne and pigmentation to psoriasis, eczema, fungal infections and hair fall — combining accurate diagnosis with practical, personalised treatment plans.";
const VANITHA_ROLE = "Dermatologist at AV Clinic, Krishnagiri";

export const dermatologyBlogs: Blog[] = [
  {
    slug: "acne-causes",
    title: "Common Causes of Acne",
    category: "Dermatology",
    excerpt:
      "Acne is common and not caused by dirt. Understand the reasons it develops and how it can be managed.",
    image: img.svcAcne,
    imageAlt: "Common causes of acne",
    author: DR_VANITHA,
    authorBio: VANITHA_BIO,
    authorRole: VANITHA_ROLE,
    date: "2026-08-29",
    modified: "2026-08-29",
    readTime: "5 min read",
    popular: true,
    tags: ["acne", "pimples", "skin care", "dermatology"],
    primaryKeyword: "causes of acne",
    secondaryKeywords: ["acne causes", "why do I get acne", "acne treatment Krishnagiri"],
    relatedService: "acne",
    relatedServiceLabel: "Acne Treatment",
    relatedArticles: ["acne-treatment", "acne-scars-treatment", "when-to-see-dermatologist"],
    sections: [
      {
        heading: "How acne forms",
        body: [
          "Acne develops when hair follicles become blocked with oil and dead skin cells. Hormonal changes, genetics, stress and certain habits can all influence how much acne a person experiences.",
        ],
      },
      {
        heading: "Factors that contribute",
        body: ["Common contributors include:"],
        list: [
          "Hormonal changes, especially during puberty and around periods",
          "Genetics and family history",
          "Excess oil production",
          "Stress",
          "Certain cosmetics or skin-care products",
        ],
      },
      {
        heading: "What does NOT cause acne",
        body: [
          "Acne is not caused by dirt, and over-washing can actually worsen it. Diet affects people differently, and chocolate or oily food is not a simple cause for everyone.",
        ],
      },
      {
        heading: "Managing acne",
        body: [
          "Acne can be managed effectively, but treatment takes time and consistency. Avoiding picking or squeezing pimples helps prevent scarring. A dermatologist can tailor a plan to your skin.",
        ],
      },
    ],
    faqs: [
      {
        q: "Does diet cause acne?",
        a: "Diet affects people differently. A balanced diet supports skin health, but specific foods are not a simple cause for everyone.",
      },
      {
        q: "Does washing my face more help acne?",
        a: "No. Over-washing can irritate skin and worsen acne. Gentle cleansing twice a day is generally enough.",
      },
    ],
    seo: {
      title: "Common Causes of Acne | AV Clinic",
      description:
        "Learn the common causes of acne and what actually works to manage it. Acne care and treatment at AV Clinic, Krishnagiri.",
    },
  },
  {
    slug: "acne-treatment",
    title: "Acne Treatment Options",
    category: "Dermatology",
    excerpt:
      "Acne is very treatable. Learn the options available and why a personalised plan works best.",
    image: img.svcAcne,
    imageAlt: "Acne treatment options",
    author: DR_VANITHA,
    authorBio: VANITHA_BIO,
    authorRole: VANITHA_ROLE,
    date: "2026-08-25",
    modified: "2026-08-25",
    readTime: "6 min read",
    tags: ["acne treatment", "pimples", "skin care", "dermatology"],
    primaryKeyword: "acne treatment",
    secondaryKeywords: ["acne treatment Krishnagiri", "how to treat acne", "acne medicine"],
    relatedService: "acne",
    relatedServiceLabel: "Acne Treatment",
    relatedArticles: ["acne-causes", "acne-scars-treatment", "skin-care-guide"],
    sections: [
      {
        heading: "Acne is treatable",
        body: [
          "Almost all acne can be improved. The right treatment depends on the type and severity of your acne, your skin type and your history.",
        ],
      },
      {
        heading: "Treatment options",
        body: ["Depending on your acne, care may include:"],
        list: [
          "Topical treatments to clear pores and reduce inflammation",
          "Oral medication where appropriate",
          "A consistent, gentle skin-care routine",
          "Guidance to prevent scarring",
        ],
      },
      {
        heading: "Why a personalised plan matters",
        body: [
          "Self-treating with harsh or wrong products can irritate skin and delay improvement. A dermatologist tailors the plan to your skin for better, safer results.",
        ],
      },
      {
        heading: "Patience is key",
        body: [
          "Acne treatment usually takes four to eight weeks to show visible improvement. Sticking to the plan and attending follow-up helps maintain results.",
        ],
      },
    ],
    faqs: [
      {
        q: "How long does acne treatment take to work?",
        a: "Most treatments show visible improvement in four to eight weeks. Consistency is important.",
      },
      {
        q: "Can acne be cured completely?",
        a: "Acne can often be well controlled, but it may recur. Ongoing guidance helps keep it in check and prevent scarring.",
      },
    ],
    seo: {
      title: "Acne Treatment Options | AV Clinic",
      description:
        "Learn about acne treatment options in Krishnagiri — topical and oral care plus a personalised plan. Dermatology care at AV Clinic.",
    },
  },
  {
    slug: "acne-scars-treatment",
    title: "Acne Scars: Treatment Options",
    category: "Dermatology",
    excerpt:
      "Scars can linger after acne clears. Learn why they form and the realistic options to improve their appearance.",
    image: img.svcAcneScars,
    imageAlt: "Acne scars treatment options",
    author: DR_VANITHA,
    authorBio: VANITHA_BIO,
    authorRole: VANITHA_ROLE,
    date: "2026-08-21",
    modified: "2026-08-21",
    readTime: "5 min read",
    tags: ["acne scars", "scar treatment", "skin care", "dermatology"],
    primaryKeyword: "acne scars treatment",
    secondaryKeywords: ["acne scar removal", "acne scars Krishnagiri", "pitted scars"],
    relatedService: "acne-scars",
    relatedServiceLabel: "Acne Scar Treatment",
    relatedArticles: ["acne-causes", "acne-treatment", "pigmentation-causes"],
    sections: [
      {
        heading: "Why acne scars form",
        body: [
          "When inflamed acne damages the deeper layers of the skin, the healing process can leave behind scars. Picking or squeezing pimples significantly increases the risk of scarring.",
        ],
      },
      {
        heading: "Types of scars",
        body: ["Scars can be:"],
        list: [
          "Depressed or pitted (atrophic)",
          "Raised (hypertrophic)",
          "Simply dark marks known as post-inflammatory hyperpigmentation",
        ],
      },
      {
        heading: "Treatment options",
        body: [
          "A dermatologist assesses the type of scars and your skin before recommending options. These may include topical care, chemical peels, resurfacing techniques and pigmentation care.",
        ],
      },
      {
        heading: "Realistic expectations",
        body: [
          "Most scars can be significantly improved, but complete removal is not guaranteed. The earlier acne is treated, the lower the chance of scarring.",
        ],
      },
    ],
    faqs: [
      {
        q: "Do dark acne marks go away on their own?",
        a: "They often fade with sun protection and skin care, but professional treatment can speed up and improve the result.",
      },
      {
        q: "Can all acne scars be treated?",
        a: "Most can be improved. The result depends on the scar type and your skin, so a dermatology assessment is the right first step.",
      },
    ],
    seo: {
      title: "Acne Scars: Treatment Options | AV Clinic",
      description:
        "Learn why acne scars form and the realistic dermatology options to improve them. Acne scar treatment at AV Clinic, Krishnagiri.",
    },
  },
  {
    slug: "pigmentation-causes",
    title: "Skin Pigmentation: Common Causes",
    category: "Dermatology",
    excerpt:
      "Dark patches and uneven skin tone are common. Understanding the cause helps guide the right approach.",
    image: img.svcPigmentation,
    imageAlt: "Common causes of skin pigmentation",
    author: DR_VANITHA,
    authorBio: VANITHA_BIO,
    authorRole: VANITHA_ROLE,
    date: "2026-08-17",
    modified: "2026-08-17",
    readTime: "5 min read",
    popular: true,
    tags: ["pigmentation", "dark spots", "uneven skin tone", "dermatology"],
    primaryKeyword: "skin pigmentation causes",
    secondaryKeywords: ["pigmentation on face", "dark spots", "uneven skin tone Krishnagiri"],
    relatedService: "pigmentation",
    relatedServiceLabel: "Pigmentation Treatment",
    relatedArticles: ["hyperpigmentation-treatment", "melasma-causes-treatment", "sun-damage-skin"],
    sections: [
      {
        heading: "Pigmentation explains uneven tone",
        body: [
          "Pigmentation occurs when certain areas of the skin produce more melanin than others. This creates dark patches, spots or overall uneven tone.",
        ],
      },
      {
        heading: "Common causes",
        body: ["Common causes include:"],
        list: [
          "Sun exposure",
          "Hormonal changes, as in melasma",
          "Past acne or inflammation",
          "Skin-care products or irritants",
          "Genetics and ageing",
        ],
      },
      {
        heading: "Sun protection is essential",
        body: [
          "Sun exposure is the biggest factor that worsens pigmentation. Regular broad-spectrum sunscreen, reapplied through the day, is essential to prevent darkening and support treatment.",
        ],
      },
      {
        heading: "Managing pigmentation",
        body: [
          "A dermatologist identifies the type of pigmentation and plans accordingly, combining topical care, professional treatment and strict sun protection with realistic expectations.",
        ],
      },
    ],
    faqs: [
      {
        q: "Can pigmentation be fully removed?",
        a: "It can usually be significantly lightened, but it may require ongoing maintenance, especially with continued sun exposure.",
      },
      {
        q: "Does sunscreen help pigmentation?",
        a: "Yes. Sunscreen is the most important step to prevent pigmentation from worsening and to support any treatment.",
      },
    ],
    seo: {
      title: "Skin Pigmentation: Common Causes | AV Clinic",
      description:
        "Learn the common causes of skin pigmentation — sun, hormones and inflammation — and how to manage it. Dermatology care at AV Clinic, Krishnagiri.",
    },
  },
  {
    slug: "hyperpigmentation-treatment",
    title: "Hyperpigmentation Treatment",
    category: "Dermatology",
    excerpt:
      "Hyperpigmentation can be managed. Learn the treatment approaches that help lighten dark spots safely.",
    image: img.svcPigmentation,
    imageAlt: "Hyperpigmentation treatment options",
    author: DR_VANITHA,
    authorBio: VANITHA_BIO,
    authorRole: VANITHA_ROLE,
    date: "2026-08-13",
    modified: "2026-08-13",
    readTime: "5 min read",
    tags: ["hyperpigmentation", "dark spots", "skin treatment", "dermatology"],
    primaryKeyword: "hyperpigmentation treatment",
    secondaryKeywords: ["hyperpigmentation treatment Krishnagiri", "lighten dark spots", "skin brightening"],
    relatedService: "pigmentation",
    relatedServiceLabel: "Pigmentation Treatment",
    relatedArticles: ["pigmentation-causes", "melasma-causes-treatment", "skin-brightening"],
    sections: [
      {
        heading: "A targeted approach",
        body: [
          "Hyperpigmentation can be lightened with the right plan. The approach depends on the cause and your skin type.",
        ],
      },
      {
        heading: "Treatment options",
        body: ["Options may include:"],
        list: [
          "Topical brightening and depigmenting products",
          "Chemical peels",
          "Professional treatments tailored to your skin",
          "Strict, consistent sun protection",
        ],
      },
      {
        heading: "What to avoid",
        body: [
          "Harsh or unverified brightening products can damage skin or cause rebound darkening. Avoid products that are not dermatologist-approved or that are very strong.",
        ],
      },
      {
        heading: "Set realistic expectations",
        body: [
          "Improvement is gradual and takes weeks to months. Maintaining sun protection and following the plan are essential for lasting results.",
        ],
      },
    ],
    faqs: [
      {
        q: "How long does hyperpigmentation treatment take?",
        a: "Results are gradual, often over weeks to months. Consistency and sun protection are key.",
      },
      {
        q: "Is sun protection necessary during treatment?",
        a: "Yes. Without daily sunscreen, treatment can be undone by sun exposure.",
      },
    ],
    seo: {
      title: "Hyperpigmentation Treatment Options | AV Clinic",
      description:
        "Learn about hyperpigmentation treatment in Krishnagiri — topical care, peels and sunscreen. Safe skin brightening at AV Clinic.",
    },
  },
  {
    slug: "melasma-causes-treatment",
    title: "Melasma: Causes and Treatment",
    category: "Aesthetic Dermatology",
    excerpt:
      "Melasma is a common, often hormonal pigmentation. Learn why it appears and how it is managed.",
    image: img.svcMelasma,
    imageAlt: "Melasma causes and treatment",
    author: DR_VANITHA,
    authorBio: VANITHA_BIO,
    authorRole: VANITHA_ROLE,
    date: "2026-08-09",
    modified: "2026-08-09",
    readTime: "5 min read",
    tags: ["melasma", "pigmentation", "hormonal", "dermatology"],
    primaryKeyword: "melasma treatment",
    secondaryKeywords: ["melasma causes", "hormonal pigmentation", "melasma Krishnagiri"],
    relatedService: "melasma",
    relatedServiceLabel: "Melasma Treatment",
    relatedArticles: ["pigmentation-causes", "hyperpigmentation-treatment", "sun-damage-skin"],
    sections: [
      {
        heading: "What melasma is",
        body: [
          "Melasma is a form of pigmentation that appears as brown or grey-brown patches, usually on the face. It is more common in women and often linked to hormonal changes.",
        ],
      },
      {
        heading: "Causes and triggers",
        body: ["Melasma is often triggered by:"],
        list: [
          "Hormonal changes such as pregnancy or hormonal medication",
          "Sun exposure",
          "Genetics",
          "Sometimes skin irritation or certain products",
        ],
      },
      {
        heading: "Managing melasma",
        body: [
          "Melasma can be persistent, so management combines daily sun protection with topical treatments and, where appropriate, professional procedures. Expectations should be realistic — it may require ongoing care.",
        ],
      },
      {
        heading: "Sun protection is non-negotiable",
        body: [
          "Consistent, broad-spectrum sunscreen, worn daily and reapplied, is the most important part of managing melasma.",
        ],
      },
    ],
    faqs: [
      {
        q: "Can melasma be cured?",
        a: "Melasma can often be significantly lightened, but it may return. Ongoing sun protection and care help keep it controlled.",
      },
      {
        q: "How long does it take to improve melasma?",
        a: "Improvement is gradual and can take months. Consistency with treatment and sunscreen is essential.",
      },
    ],
    seo: {
      title: "Melasma: Causes & Treatment | AV Clinic",
      description:
        "Understand melasma — a common hormonal pigmentation — its causes and management. Dermatology care at AV Clinic, Krishnagiri.",
    },
  },
  {
    slug: "skin-allergy-types",
    title: "Common Types of Skin Allergies",
    category: "Dermatology",
    excerpt:
      "Skin allergies come in many forms. Learn the common types and what triggers them.",
    image: img.svcSkinAllergies,
    imageAlt: "Common types of skin allergies",
    author: DR_VANITHA,
    authorBio: VANITHA_BIO,
    authorRole: VANITHA_ROLE,
    date: "2026-08-05",
    modified: "2026-08-05",
    readTime: "5 min read",
    tags: ["skin allergy", "allergy", "dermatitis", "dermatology"],
    primaryKeyword: "types of skin allergies",
    secondaryKeywords: ["skin allergy", "allergic skin reaction", "dermatitis"],
    relatedService: "skin-allergies",
    relatedServiceLabel: "Skin Allergy Care",
    relatedArticles: ["skin-allergy-symptoms", "eczema-causes-management", "sensitive-skin-care"],
    sections: [
      {
        heading: "Not all rashes are the same",
        body: [
          "Skin allergies occur when the immune system reacts to a substance. They can appear in several ways, and identifying the type helps management.",
        ],
      },
      {
        heading: "Common types",
        body: ["Common skin allergy types include:"],
        list: [
          "Contact dermatitis — from touching an irritant or allergen",
          "Urticaria (hives) — raised, itchy welts",
          "Atopic dermatitis — an allergic-condition type of eczema",
          "Drug rashes — from medication",
        ],
      },
      {
        heading: "Common triggers",
        body: [
          "Triggers vary by person and may include cosmetics, detergents, fragrances, metals (like nickel), certain plants, foods and medications.",
        ],
      },
      {
        heading: "Getting it checked",
        body: [
          "If a rash is persistent, painful or widespread, a dermatologist can identify the type and its trigger and guide appropriate care.",
        ],
      },
    ],
    faqs: [
      {
        q: "Are skin allergies contagious?",
        a: "No. Skin allergies are not contagious and cannot be spread to others.",
      },
      {
        q: "Can a skin allergy start suddenly?",
        a: "Yes. Allergic reactions can appear at any time, even to things you have used before.",
      },
    ],
    seo: {
      title: "Common Types of Skin Allergies | AV Clinic",
      description:
        "Learn the common types and triggers of skin allergies and when to see a dermatologist. Allergy care at AV Clinic, Krishnagiri.",
    },
  },
  {
    slug: "skin-allergy-symptoms",
    title: "Skin Allergy Symptoms to Watch For",
    category: "Dermatology",
    excerpt:
      "Itching, redness and rashes are common signs of a skin allergy. Learn the symptoms and what to do.",
    image: img.svcSkinAllergies,
    imageAlt: "Skin allergy symptoms",
    author: DR_VANITHA,
    authorBio: VANITHA_BIO,
    authorRole: VANITHA_ROLE,
    date: "2026-08-01",
    modified: "2026-08-01",
    readTime: "5 min read",
    tags: ["skin allergy", "itching", "rash", "dermatology"],
    primaryKeyword: "skin allergy symptoms",
    secondaryKeywords: ["allergic rash", "itching skin", "skin reaction symptoms"],
    relatedService: "skin-allergies",
    relatedServiceLabel: "Skin Allergy Care",
    relatedArticles: ["skin-allergy-types", "eczema-causes-management", "skin-infection-warning-signs"],
    sections: [
      {
        heading: "How skin allergies show up",
        body: [
          "Skin allergies commonly cause itching, redness, swelling, a rash or small bumps. The symptoms depend on the type of reaction and where it occurs.",
        ],
      },
      {
        heading: "Common symptoms",
        body: ["Watch for these signs:"],
        list: [
          "Itching or burning",
          "Redness or swelling",
          "Raised welts or a bumpy rash",
          "Dry, flaking or scaly skin",
          "Blisters or oozing, in more intense reactions",
        ],
      },
      {
        heading: "When to seek care",
        body: [
          "If symptoms are severe, widespread, painful or persistent, or if they come with breathing difficulty or swelling of the face or lips, seek medical care promptly.",
        ],
      },
      {
        heading: "Managing symptoms",
        body: [
          "Avoiding the trigger, gentle skin care and appropriate topical treatment can help. A dermatologist can confirm the cause and guide care.",
        ],
      },
    ],
    faqs: [
      {
        q: "How long do skin allergy symptoms last?",
        a: "This depends on the cause and whether the trigger is avoided. Some settle quickly, while others persist.",
      },
      {
        q: "When is a skin allergy an emergency?",
        a: "Seek urgent care if a reaction comes with breathing difficulty or swelling of the face or lips.",
      },
    ],
    seo: {
      title: "Skin Allergy Symptoms to Watch For | AV Clinic",
      description:
        "Recognise the symptoms of skin allergies — itching, rash and swelling — and when to seek care. Dermatology advice at AV Clinic, Krishnagiri.",
    },
  },
  {
    slug: "eczema-causes-management",
    title: "Eczema: Causes and Management",
    category: "Dermatology",
    excerpt:
      "Eczema is a common, chronic skin condition. Learn what triggers it and how it can be managed.",
    image: img.svcEczema,
    imageAlt: "Eczema causes and management",
    author: DR_VANITHA,
    authorBio: VANITHA_BIO,
    authorRole: VANITHA_ROLE,
    date: "2026-07-28",
    modified: "2026-07-28",
    readTime: "5 min read",
    tags: ["eczema", "dermatitis", "itchy skin", "dermatology"],
    primaryKeyword: "eczema management",
    secondaryKeywords: ["eczema causes", "atopic dermatitis", "itchy skin treatment"],
    relatedService: "eczema",
    relatedServiceLabel: "Eczema Care",
    relatedArticles: ["skin-allergy-types", "dry-skin-causes-care", "sensitive-skin-care"],
    sections: [
      {
        heading: "What eczema is",
        body: [
          "Eczema (dermatitis) is a chronic condition causing dry, itchy and inflamed skin. It is common at any age and flare-ups can be triggered by irritants, allergens, weather and stress.",
        ],
      },
      {
        heading: "Common triggers",
        body: ["Triggers vary and may include:"],
        list: [
          "Soaps, detergents and harsh skin-care products",
          "Certain foods or allergens",
          "Weather changes",
          "Stress",
          "Dry skin and scratching",
        ],
      },
      {
        heading: "Daily management",
        body: [
          "Gentle cleansing, regular moisturising and avoiding known triggers are the foundation of eczema care. Keeping skin hydrated and using fragrance-free products helps reduce flare-ups.",
        ],
      },
      {
        heading: "When to see a dermatologist",
        body: [
          "If eczema is severe, widespread or not improving with basic care, a dermatologist can help manage symptoms and identify triggers to improve comfort and control.",
        ],
      },
    ],
    faqs: [
      {
        q: "Is eczema contagious?",
        a: "No. Eczema is not contagious and cannot be passed to another person.",
      },
      {
        q: "Can eczema be cured?",
        a: "There is no permanent cure, but eczema can usually be well controlled with the right daily care and treatment.",
      },
    ],
    seo: {
      title: "Eczema: Causes & Management | AV Clinic",
      description:
        "Understand eczema, its triggers and how to manage it. Dermatology care for itchy, inflamed skin at AV Clinic, Krishnagiri.",
    },
  },
  {
    slug: "psoriasis-symptoms-treatment",
    title: "Psoriasis: Symptoms and Treatment",
    category: "Dermatology",
    excerpt:
      "Psoriasis is a chronic skin condition. Learn its symptoms and how it is managed.",
    image: img.svcPsoriasis,
    imageAlt: "Psoriasis symptoms and treatment",
    author: DR_VANITHA,
    authorBio: VANITHA_BIO,
    authorRole: VANITHA_ROLE,
    date: "2026-07-24",
    modified: "2026-07-24",
    readTime: "5 min read",
    tags: ["psoriasis", "scaly skin", "chronic skin", "dermatology"],
    primaryKeyword: "psoriasis treatment",
    secondaryKeywords: ["psoriasis symptoms", "psoriasis", "psoriasis management"],
    relatedService: "psoriasis",
    relatedServiceLabel: "Psoriasis Care",
    relatedArticles: ["eczema-causes-management", "skin-allergy-types", "when-to-see-dermatologist"],
    sections: [
      {
        heading: "What psoriasis is",
        body: [
          "Psoriasis is a chronic condition in which the immune system causes skin cells to multiply too quickly, leading to red, scaly patches. It can affect the scalp, elbows, knees and other areas.",
        ],
      },
      {
        heading: "Symptoms",
        body: ["Common symptoms include:"],
        list: [
          "Red patches with silvery scales",
          "Itching, dryness or soreness",
          "Patches on the scalp, elbows, knees and nails",
          "Symptoms that come and go",
        ],
      },
      {
        heading: "Triggers",
        body: [
          "Flare-ups can be triggered by stress, infections, certain medications, skin injury and sometimes cold weather. Identifying your triggers helps keep the condition controlled.",
        ],
      },
      {
        heading: "Treatment",
        body: [
          "Care focuses on reducing scaling and inflammation, controlling itch and preventing flare-ups. A dermatologist tailors treatment to the severity of your psoriasis and provides long-term support.",
        ],
      },
    ],
    faqs: [
      {
        q: "Is psoriasis contagious?",
        a: "No. Psoriasis is not contagious and cannot spread from person to person.",
      },
      {
        q: "Is psoriasis curable?",
        a: "There is no permanent cure, but psoriasis can often be well controlled, allowing most people to live comfortably.",
      },
    ],
    seo: {
      title: "Psoriasis: Symptoms & Treatment | AV Clinic",
      description:
        "Learn about psoriasis, its symptoms and how it is managed by a dermatologist. Psoriasis care at AV Clinic, Krishnagiri.",
    },
  },
  {
    slug: "fungal-skin-infections",
    title: "Common Fungal Skin Infections",
    category: "Dermatology",
    excerpt:
      "Fungal infections of the skin are common and treatable. Learn the types and how they are cared for.",
    image: img.svcFungal,
    imageAlt: "Common fungal skin infections",
    author: DR_VANITHA,
    authorBio: VANITHA_BIO,
    authorRole: VANITHA_ROLE,
    date: "2026-07-20",
    modified: "2026-07-20",
    readTime: "5 min read",
    tags: ["fungal infection", "ringworm", "dermatology", "skin infection"],
    primaryKeyword: "fungal skin infection",
    secondaryKeywords: ["fungal skin infection treatment", "ringworm", "tinea"],
    relatedService: "fungal-infections",
    relatedServiceLabel: "Fungal Infection Care",
    relatedArticles: ["skin-infection-warning-signs", "dandruff-causes-treatment", "when-to-see-dermatologist"],
    sections: [
      {
        heading: "Fungal infections are common",
        body: [
          "Fungal infections of the skin are widespread and usually easily treated. They often occur in warm, moist areas of the body and are not caused by poor hygiene alone.",
        ],
      },
      {
        heading: "Common types",
        body: ["Common fungal infections include:"],
        list: [
          "Ringworm (tinea) — a ring-shaped rash",
          "Athlete's foot — between the toes",
          "Fungal nail infections",
          "Jock itch — in the groin",
        ],
      },
      {
        heading: "Prevention",
        body: [
          "Keeping skin clean and dry, wearing breathable clothing, not sharing towels or footwear and treating fungal infections early help prevent spread.",
        ],
      },
      {
        heading: "Treatment",
        body: [
          "Fungal infections usually respond to appropriate antifungal treatment. A dermatologist can confirm the diagnosis and prescribe the correct treatment, since not all rashes are fungal.",
        ],
      },
    ],
    faqs: [
      {
        q: "Are fungal skin infections contagious?",
        a: "Some, such as ringworm, are contagious and can spread through close contact or shared items.",
      },
      {
        q: "Can a fungal infection clear on its own?",
        a: "Some may, but most benefit from appropriate treatment to clear completely and prevent spread.",
      },
    ],
    seo: {
      title: "Common Fungal Skin Infections | AV Clinic",
      description:
        "Learn about common fungal skin infections — types, prevention and treatment. Dermatology care at AV Clinic, Krishnagiri.",
    },
  },
  {
    slug: "skin-infection-warning-signs",
    title: "Skin Infection Warning Signs",
    category: "Dermatology",
    excerpt:
      "Some skin problems signal an infection needing prompt care. Learn the warning signs to watch for.",
    image: img.svcFungal,
    imageAlt: "Skin infection warning signs",
    author: DR_VANITHA,
    authorBio: VANITHA_BIO,
    authorRole: VANITHA_ROLE,
    date: "2026-07-16",
    modified: "2026-07-16",
    readTime: "4 min read",
    tags: ["skin infection", "warning signs", "dermatology", "redness"],
    primaryKeyword: "skin infection warning signs",
    secondaryKeywords: ["infected skin", "skin redness swelling", "skin infection signs"],
    relatedService: "general-dermatology",
    relatedServiceLabel: "Dermatology Care",
    relatedArticles: ["fungal-skin-infections", "skin-allergy-symptoms", "symptoms-not-to-ignore"],
    sections: [
      {
        heading: "Recognising infection early",
        body: [
          "A skin infection can start small but spread if untreated. Knowing the warning signs helps you act early and avoid complications.",
        ],
      },
      {
        heading: "Warning signs",
        body: ["See a doctor if you notice:"],
        list: [
          "Increasing redness, swelling or warmth",
          "Pain that is getting worse",
          "Pus, oozing or weeping",
          "A spreading rash or streak",
          "Fever with a skin problem",
        ],
      },
      {
        heading: "Why early care matters",
        body: [
          "Infected skin can worsen quickly. Early treatment is more comfortable, simpler and prevents serious complications.",
        ],
      },
      {
        heading: "Avoid self-treatment",
        body: [
          "Harsh or inappropriate products can worsen an infection. If you suspect an infection, seek professional diagnosis and treatment.",
        ],
      },
    ],
    faqs: [
      {
        q: "When should I see a doctor for a skin problem?",
        a: "If there is spreading redness, pain, pus or fever, or if a problem is not improving, seek care.",
      },
      {
        q: "Can a skin infection be serious?",
        a: "Some can be, if left untreated. Early recognition and treatment are the safest approach.",
      },
    ],
    seo: {
      title: "Skin Infection Warning Signs | AV Clinic",
      description:
        "Learn the warning signs of a skin infection and when to seek care. Dermatology guidance at AV Clinic, Krishnagiri.",
    },
  },
  {
    slug: "dry-skin-causes-care",
    title: "Dry Skin: Causes and Care",
    category: "Skin Care",
    excerpt:
      "Dry, tight skin is common and usually manageable. Learn the causes and how to care for your skin barrier.",
    image: img.svcSkinRejuvenation,
    imageAlt: "Dry skin causes and care",
    author: DR_VANITHA,
    authorBio: VANITHA_BIO,
    authorRole: VANITHA_ROLE,
    date: "2026-07-12",
    modified: "2026-07-12",
    readTime: "4 min read",
    tags: ["dry skin", "skin care", "moisturiser", "dermatology"],
    primaryKeyword: "dry skin care",
    secondaryKeywords: ["dry skin causes", "how to moisturise", "flaky skin"],
    relatedService: "general-dermatology",
    relatedServiceLabel: "Dermatology Care",
    relatedArticles: ["sensitive-skin-care", "eczema-causes-management", "healthy-diet-skin"],
    sections: [
      {
        heading: "Why skin becomes dry",
        body: [
          "Dry skin happens when the skin loses moisture or its protective barrier is weakened. It can feel tight, rough or flaky, and can become itchy.",
        ],
      },
      {
        heading: "Common causes",
        body: ["Causes include:"],
        list: [
          "Hot showers and harsh soaps",
          "Cold, dry weather or low humidity",
          "Ageing",
          "Certain medical conditions",
          "Not using enough moisturiser",
        ],
      },
      {
        heading: "Daily care that helps",
        body: [
          "Use lukewarm water, gentle fragrance-free cleansers and a good moisturiser soon after washing. Applying moisturiser while skin is slightly damp locks in hydration.",
        ],
      },
      {
        heading: "When to see a dermatologist",
        body: [
          "If dryness is severe, painful or accompanied by itching, flaking or a rash, a dermatologist can check for an underlying cause and guide care.",
        ],
      },
    ],
    faqs: [
      {
        q: "How often should I moisturise dry skin?",
        a: "At least once or twice a day, ideally after washing while the skin is still damp.",
      },
      {
        q: "Does drinking water cure dry skin?",
        a: "Hydration helps overall health, but moisturising and gentle skin care are the main ways to manage dry skin.",
      },
    ],
    seo: {
      title: "Dry Skin: Causes & Care | AV Clinic",
      description:
        "Learn the causes of dry skin and how to care for your skin barrier. Dermatology skin-care guidance at AV Clinic, Krishnagiri.",
    },
  },
  {
    slug: "sensitive-skin-care",
    title: "Sensitive Skin Care Tips",
    category: "Skin Care",
    excerpt:
      "Sensitive skin needs a gentle, consistent routine. Learn the care habits that protect it and reduce irritation.",
    image: img.svcSkinRejuvenation,
    imageAlt: "Sensitive skin care tips",
    author: DR_VANITHA,
    authorBio: VANITHA_BIO,
    authorRole: VANITHA_ROLE,
    date: "2026-07-08",
    modified: "2026-07-08",
    readTime: "4 min read",
    tags: ["sensitive skin", "skin care", "gentle routine", "dermatology"],
    primaryKeyword: "sensitive skin care",
    secondaryKeywords: ["sensitive skin tips", "gentle skin care", "skin irritation"],
    relatedService: "skin-allergies",
    relatedServiceLabel: "Sensitive Skin Care",
    relatedArticles: ["dry-skin-causes-care", "skin-allergy-types", "eczema-causes-management"],
    sections: [
      {
        heading: "Gentle is the goal",
        body: [
          "Sensitive skin reacts easily to irritants, weather and certain products. The key is a simple, gentle, consistent routine rather than harsh or multifunctional products.",
        ],
      },
      {
        heading: "Important habits",
        body: ["These practices help sensitive skin:"],
        list: [
          "Use fragrance-free, gentle cleansers",
          "Patch-test new products",
          "Moisturise regularly to support the skin barrier",
          "Wear sunscreen daily and reapply",
          "Avoid over-exfoliating and hot water",
        ],
      },
      {
        heading: "Keep it simple",
        body: [
          "Fewer, milder products are often better for sensitive skin. Introducing one product at a time helps you identify anything that causes a reaction.",
        ],
      },
      {
        heading: "When to seek guidance",
        body: [
          "If your skin is persistently irritated, red or reactive, a dermatologist can help identify triggers and recommend a suitable routine.",
        ],
      },
    ],
    faqs: [
      {
        q: "What should sensitive skin avoid?",
        a: "Fragrances, strong exfoliants, hot water and harsh soaps can irritate sensitive skin.",
      },
      {
        q: "Is sunscreen safe for sensitive skin?",
        a: "Yes. Look for fragrance-free, gentle sunscreen. Sunscreen is essential even for sensitive skin.",
      },
    ],
    seo: {
      title: "Sensitive Skin Care Tips | AV Clinic",
      description:
        "Learn gentle skin-care tips for sensitive skin — cleansers, moisturising and sun protection. Dermatology advice at AV Clinic, Krishnagiri.",
    },
  },
  {
    slug: "when-to-see-dermatologist",
    title: "When Should You See a Dermatologist?",
    category: "Dermatology",
    excerpt:
      "Not every skin problem needs a specialist, but many do. Learn when a dermatology visit is worthwhile.",
    image: img.svcGeneralDerma,
    imageAlt: "When to see a dermatologist",
    author: DR_VANITHA,
    authorBio: VANITHA_BIO,
    authorRole: VANITHA_ROLE,
    date: "2026-07-04",
    modified: "2026-07-04",
    readTime: "5 min read",
    tags: ["dermatologist", "skin specialist", "skin care", "dermatology"],
    primaryKeyword: "when to see a dermatologist",
    secondaryKeywords: ["dermatologist Krishnagiri", "skin specialist", "skin problem"],
    relatedService: "general-dermatology",
    relatedServiceLabel: "Dermatology Care",
    relatedArticles: ["acne-causes", "pigmentation-causes", "hair-loss-causes"],
    sections: [
      {
        heading: "Your skin deserves expert attention",
        body: [
          "Your skin is your largest organ and reflects both your health and daily environment. While many minor concerns resolve on their own, persistent or recurring problems often need specialist evaluation.",
        ],
      },
      {
        heading: "Common reasons to see a dermatologist",
        body: ["Consider a visit if you have:"],
        list: [
          "Persistent or severe acne",
          "Pigmentation or melasma that bothers you",
          "Rashes, itching or flaking that keeps returning",
          "Hair fall, thinning or scalp irritation",
          "A mole or lesion that changes in size, shape or colour",
          "Fungal infections, warts, skin tags or nail changes",
        ],
      },
      {
        heading: "What a consultation involves",
        body: [
          "A dermatology consultation begins with a discussion of your concerns and history. The specialist examines your skin and, when needed, recommends simple investigations to confirm the diagnosis.",
        ],
      },
      {
        heading: "The earlier, the better",
        body: [
          "Many skin conditions respond better when addressed early. If a skin concern is affecting your comfort or confidence, a specialist consultation is a worthwhile first step.",
        ],
      },
    ],
    faqs: [
      {
        q: "Do I need a referral to see a dermatologist?",
        a: "In most cases you can book a dermatology consultation directly without a referral.",
      },
      {
        q: "Should I see a dermatologist for a mole that changed?",
        a: "Yes. A mole that changes in size, shape or colour should be evaluated by a dermatologist.",
      },
    ],
    seo: {
      title: "When to See a Dermatologist in Krishnagiri | AV Clinic",
      description:
        "Learn when to see a dermatologist in Krishnagiri — acne, pigmentation, hair fall and changing moles. Specialist care at AV Clinic.",
    },
  },
];